import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";

interface Conversation {
	id: string;
	title: string;
	mode: string;
	model: string;
	createdAt: string;
	updatedAt: string;
}

interface Message {
	id: string;
	conversationId: string;
	role: string;
	content: string;
	model: string | null;
	tokensUsed: number | null;
	toolCalls: Record<string, unknown> | null;
	toolResults: Record<string, unknown> | null;
	isError: boolean;
	createdAt: string;
}

export function useConversationList() {
	return useQuery({
		queryKey: ["conversations"],
		queryFn: async () => {
			const data = await api.get<{ items: Conversation[] }>("/api/ai/conversations");
			return data;
		},
	});
}

export function useConversation(id: string | undefined) {
	return useQuery({
		queryKey: ["conversations", id],
		queryFn: async () => {
			if (!id) return null;
			const data = await api.get<{ conversation: Conversation; messages: Message[] }>(
				`/api/ai/conversations/${id}`,
			);
			return data;
		},
		enabled: !!id,
	});
}

export function useCreateConversation() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (body: {
			title?: string;
			mode?: string;
			model?: string;
			systemPrompt?: string;
		}) => {
			const data = await api.post<Conversation>("/api/ai/conversations", body);
			return data;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["conversations"] });
		},
	});
}

export function useSendMessage() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async ({ id, content }: { id: string; content: string }) => {
			const data = await api.post<{ jobId: string; status: string }>(
				`/api/ai/conversations/${id}/messages`,
				{
					content,
				},
			);
			return data;
		},
		onSuccess: (_data, variables) => {
			queryClient.invalidateQueries({ queryKey: ["conversations", variables.id] });
		},
	});
}

export function useDeleteConversation() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (id: string) => {
			await api.delete(`/api/ai/conversations/${id}`);
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["conversations"] });
		},
	});
}
